import React, { useEffect, useState, useRef } from 'react';
import { useProjectScenarios } from '../../../context/ProjectScenariosContext';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import { ProjectAssumption } from '../../../models/ProjectAssumption';
import ScenarioSwitcher from './ScenarioSwitcher';
import { ProjectsScenariosRestDataProvider } from '../../../data/ProjectScenariosDataProvider';
import { useTranslation } from 'react-i18next';

function generateGuid() {
  // RFC4122 version 4 compliant UUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

const ProjectAssumptions: React.FC<{ scenarioId: string; setScenarioId: (id: string) => void; onDirtyChange?: (dirty: boolean) => void }> = ({ scenarioId, setScenarioId, onDirtyChange }) => {
  const { t } = useTranslation(['common', 'screens']);
  const { scenarios, setScenarios } = useProjectScenarios();
  const scenario = scenarios.find(s => s.id === scenarioId);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const provider = React.useRef(new ProjectsScenariosRestDataProvider()).current;

  React.useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  const columns: DetailedListColumn<ProjectAssumption>[] = React.useMemo(() => [
    { key: 'description', header: t('common:description'), type: 'multiline', multiline: true, rows: 3, required: true },
  ], [t]);

  if (!scenario) return <div>{t('screens:projects.noScenarioFound')}</div>;

  // Handle edits: update global scenario state immediately
  const handleChange = (items: ProjectAssumption[]) => {
    const itemsWithIds = items.map(item => ({ ...item, id: item.id || generateGuid() }));
    setScenarios(scenarios.map(s => s.id === scenarioId ? { ...s, assumptions: itemsWithIds } : s));
    setIsDirty(true);
  };

  // Save handler for ScenarioSwitcher
  const handleSave = async () => {
    if (!scenario.id) return;
    setSaving(true);
    try {
      await provider.update(scenario.id, { ...scenario, assumptions: scenario.assumptions });
      setIsDirty(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ScenarioSwitcher scenarioId={scenarioId} setScenarioId={setScenarioId} onSave={handleSave} saving={saving} />
      <SubsectionPanel title={t('screens:projectAssumptions.title')}>
        <DetailedListComponent
          columns={columns}
          items={scenario.assumptions || []}
          rowKey={a => a.id}
          onChange={items => handleChange(items as ProjectAssumption[])}
          editRole="Manager,Admin"
          addRole="Manager,Admin"
          deleteRole="Manager,Admin"
        />
        {saving && <div style={{ marginTop: 12 }}>{t('common:saving')}</div>}
      </SubsectionPanel>
    </>
  );
};

export default ProjectAssumptions;
